import { QrCodeService } from './../data/qr-code.service';
import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { SignalRService } from '../data/signalr.service';
import { Observable, Subject, Subscription, firstValueFrom, interval, map, shareReplay, startWith, switchMap, take, takeUntil, takeWhile, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { QrCodeDto } from '../models/dtos/qr-code-dto';
import { AuthService } from '../core/authentication/auth.service';

@Component({
  selector: 'app-qr-code',
  templateUrl: './qr-code.component.html',
  styleUrls: ['./qr-code.component.scss'],
  standalone: false
})
export class QrCodeComponent implements OnInit, OnDestroy {
  private signalRService = inject(SignalRService);
  private qrCodeService = inject(QrCodeService);
  private authService = inject(AuthService);

  @Input() size: number = 512;
  @Input() level: 'L' | 'M' | 'Q' | 'H' = 'L';

  public timeLeft: number = 0;
  public loadingProgress: number = 0;
  public qrCodeUrl: string | undefined;

  private startDate: Date;
  private endDate: Date;
  private hubName: string = 'QrCodeHub';
  private retryConnectionDelay: number = 5000;
  private retryConnectionCount: number = 120;
  private baseUrl: string;
  private workerId: number | undefined;
  private connectionCheckInterval: ReturnType<typeof setInterval>;
  private countdownInterval: Subscription | undefined;
  private qrRecieverSubscription: Subscription | undefined;
  private readonly reconnectCancelation$ = new Subject<void>();


  constructor() {
    this.baseUrl = environment.baseUrl + 'logwork/';
  }

  async ngOnInit(): Promise<void> {
    this.workerId = await firstValueFrom(this.authService.getWorkerIdByAuthServerUserId());

    if (this.workerId) {
      this.startSignalRConnection();
    }
  }

  isProduction(): boolean {
    return environment.production;
  }

  ngOnDestroy(): void {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
    }

    if (this.countdownInterval) {
      this.countdownInterval.unsubscribe();
    }

    if (this.qrRecieverSubscription) {
      this.qrRecieverSubscription.unsubscribe();
    }

    this.reconnectCancelation$.next();
    this.reconnectCancelation$.unsubscribe();
  }

  private startSignalRConnection(): Observable<void> {
    if (this.qrRecieverSubscription) {
      this.qrRecieverSubscription.unsubscribe();
      this.qrRecieverSubscription = undefined;
    }

    const connected$ = this.signalRService.startConnection(this.hubName, this.workerId.toString(), { onclose: async () => await this.reconnect() })
      .pipe(shareReplay());

    this.qrRecieverSubscription = connected$
      .pipe(tap(() => this.getFirstActiveCode()))
      .pipe(switchMap(() => this.signalRService.receiveQrCode()))
      .subscribe((qr: QrCodeDto) => this.updateQrCode(qr));

    return connected$;
  }

  private getFirstActiveCode(): void {
    this.qrCodeService.getQrCodes(this.workerId).subscribe({
      next: (qr: QrCodeDto) => this.updateQrCode(qr),
      error: (err) => console.error('Failed to retrieve QR codes', err)
    });
  }

  private resetProgressBar() {
    this.loadingProgress = 0;
    this.timeLeft = 0;
  }

  private async reconnect(): Promise<void> {
    await firstValueFrom(
      interval(this.retryConnectionDelay)
        .pipe(startWith(0))
        .pipe(map(() => this.startSignalRConnection()))
        .pipe(takeWhile(() => this.signalRService.isConnected(this.hubName)))
        .pipe(takeUntil(this.reconnectCancelation$))
        .pipe(take(this.retryConnectionCount)));
  }

  private updateQrCode(qrCodeDto: QrCodeDto): void {
    if (!!qrCodeDto) {
      this.resetProgressBar();
      this.qrCodeUrl = `${this.baseUrl}${qrCodeDto.Code}`;
      this.startDate = qrCodeDto.ActivityStartDate;
      this.endDate = qrCodeDto.ActivityEndDate;
      this.startCountdown();
    }
  }

  private startCountdown(): void {
    this.countdownInterval?.unsubscribe();

    const startTime = this.startDate.getTime();
    const endTime = this.endDate.getTime();
    const totalTime = (endTime - startTime) / 1000;

    this.timeLeft = Math.floor((endTime - Date.now()) / 1000);
    this.updateProgress(totalTime);

    this.countdownInterval = interval(1000).subscribe(() => {
      this.timeLeft--;
      this.updateProgress(totalTime);

      if (this.timeLeft <= 0) {
        this.countdownInterval?.unsubscribe();
        this.loadingProgress = 100;
      }
    });
  }

  private updateProgress(totalTime: number): void {
    this.loadingProgress = ((totalTime - this.timeLeft) / totalTime) * 100;
  }
}